
import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AppContext } from '../../app-context';
import IDialogComponent from '../../components/dialog/i-dialog.component';
import IGridComponent from '../../components/grid/i-grid.component';
import TitlePanel from '../../components/title-panel/title-panel.component';
import { ALL, EMPTY_STRING, PAGE_SIZE, TYPE_LABOR, TYPE_MATERIAL, TYPE_PROCESS } from '../common/constants';
import './master-data-list.component.scss';
import { deleteMasterData, searchMasterData, exportMasterData } from './masterdata.services';
import SpecsComponent from './specs/specs.component';
import MasterDataSearchPanel from './master-data-search-panel.component';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { LABOR_SPECS_MAP, MATERIAL_SPECS_MAP, PROCESS_SPECS_MAP } from './specs/specs.mapping';
import { isChecking, isPacking } from '../common/util.services';

const MasterDataList = () => {
  const appContext = useContext(AppContext);
  const navigate = useNavigate();
  const rows: GridRowsProp = [];
  const [role, setRole] = useState<String>('USER');
  const [masterData, setMasterData] = useState(rows);
  const [open, setOpen] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [searchData, setSearchData] = useState({ searchText: EMPTY_STRING, type: ALL, input: ALL, isLatestOnly: false, 
                                                 latestOnly: 0, supplier: ALL, specParams: null });
  const columns: GridColDef[] = [
    { field: 'input', headerName: 'Input', filterable: false, sortable: false, width: 175 },
    { field: 'code', headerName: 'Code', filterable: false, sortable: false, width: 90 },
    { field: 'description', headerName: 'Description', filterable: false, sortable: false, width: 175 },
    {
      field: 'specs', headerName: 'Specs', filterable: false, sortable: false, width: 70,
      renderCell: (params) => { return <div className="cell-link" onClick={() => { openSpecsPopup(params) }}>Specs</div>}
    },
    { field: 'uom', headerName: 'UOM', filterable: false, sortable: false, width: 70 },
    { field: 'unitCost', headerName: 'Unit Cost', filterable: false, sortable: false, width: 80 },
    { field: 'quoteLink', headerName: 'Quote', filterable: false, sortable: false, width: 90,
      renderCell: (params) => { return params.row.quoteLink.includes('drive.google.com') ?
                                (<Link to={params.row.quoteLink} target="_blank">Quote</Link>) : 'NA' }
    },
    { field: 'supplier', headerName: 'Supplier', filterable: false, sortable: false, width: 200 },
    { field: 'version', headerName: 'Version', filterable: false, sortable: false, width: 70 },
    { field: 'moq', headerName: 'MOQ', filterable: false, sortable: false, width: 70 },
    {
      field: 'processLossPercentage', headerName: 'PL',
      valueFormatter: (processLossPercentage: any) => processLossPercentage + '%', filterable: false, sortable: false, width: 50
    },
    { field: 'remarks', headerName: 'Remarks', filterable: false, sortable: false, width: 130 }
  ];
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);

  useEffect(() => {
    if (appContext.userData) {
      setRole(appContext.userData.role);
    }
    setLoading(true);
    searchMasterData(searchData, page, pageSize, appContext,
      (response: any) => {
        setMasterData(response.data.data);
        setTotalRows(response.data.totalElements);
        setLoading(false);
      }, (error: any) => {
        console.log(error);
        setLoading(false);
      });
  }, [searchData, page, pageSize]);

  const handleEditAction = (row: any) => {
    navigate(EMPTY_STRING + row.rowId);
  };

  const createCopy = (row: any) => {
    navigate(EMPTY_STRING + row.rowId, {state: {isCopy: true}});
  };

  const handleDeleteAction = (row: any) => {
    deleteMasterData(row.rowId, appContext,
      (response: any) => {
        setMasterData(masterData.filter((r) => r.rowId !== row.rowId));
        appContext.setSuccessMessage(response.message)
        appContext.setShowLoader(false);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message)
      });
  };

  const addMasterData = () => {
    navigate('new');
  }

  const handleClose = () => {
    setOpen(false);
  };

  const openSpecsPopup = (params: any) => {
    setRowData(params.row);
    setOpen(true);
  }

  const onPaginationModelChange = (evt: any) => {
    setPage(evt.page);
    setPageSize(evt.pageSize);
  }

  const search = (searchData: any) => {
    setSearchData(searchData);
  }

  const getSpecHeaders = () => {
    let type = searchData.type;
    let input = searchData.input;
    if(TYPE_MATERIAL === type) {
      return MATERIAL_SPECS_MAP[input];
    } else if(TYPE_PROCESS === type) {
      return PROCESS_SPECS_MAP[input];
    } else if(TYPE_LABOR === type) {
      if(isChecking(input)) {
        return LABOR_SPECS_MAP['Checking'];
      } else if(isPacking(input)) {
        return LABOR_SPECS_MAP['Packing'];
      } else {
        return LABOR_SPECS_MAP['Bale Packing'];
      }
    }
  }

  const saveExcel = (inputData: any) => {
    let specHeaders = getSpecHeaders();

    let header = ["Row Id", "Type", "Input", "Code", "Description", specHeaders[0].fieldName, specHeaders[1].fieldName, specHeaders[2].fieldName,
                  specHeaders[3].fieldName, specHeaders[4].fieldName, specHeaders[5].fieldName, specHeaders[6].fieldName, specHeaders[7].fieldName,
                  specHeaders[8].fieldName, specHeaders[9].fieldName, specHeaders[10].fieldName, specHeaders[11].fieldName, 
                  'Fabric' === searchData.input ? specHeaders[12].fieldName : 'NA',
                  'Fabric' === searchData.input ? specHeaders[13].fieldName : 'NA',
                  'Fabric' === searchData.input ? specHeaders[14].fieldName : 'NA',
                  "SKU", "Supplier", "UOM", "Currency", "Unit Cost", "PL (%)", "MOQ", "Quote Link", "Remarks"];
    
    const excelSheet = XLSX.utils.book_new();
    XLSX.utils.sheet_add_aoa(excelSheet, [header]);
    XLSX.utils.sheet_add_json(excelSheet, inputData, { origin: 'A2', skipHeader: true });
    const excelBook = { Sheets: { 'Input Data': excelSheet }, SheetNames: ['Input Data'] };
    const excelBuffer = XLSX.write(excelBook, { bookType: 'xlsx', type: 'array', cellStyles:true });
    const excelData = new Blob([excelBuffer], { type: 'xlsx' });
    FileSaver.saveAs(excelData, searchData.input + ".xlsx");
  }

  const exportData = () => {
    if(searchData.type === ALL || searchData.input === ALL) {
      appContext.setDialogMessage('Select type and input.');
    } else {
      exportMasterData(searchData, appContext,
        (response: any) => {
          saveExcel(response.data);
          setLoading(false);
        }, (error: any) => {
          console.log(error);
          setLoading(false);
        });
    }
  }

  const getMainPanel = () => {
    return <>
      <MasterDataSearchPanel search={search} searchData={searchData} costing={false} showExportButton={true} export={exportData}/>
      <IGridComponent title={EMPTY_STRING} rows={masterData} columns={columns} loading={loading}
        paginationType="server" paginationModelChange={onPaginationModelChange} totalRows={totalRows}
        showAction={true} handleDeleteAction={handleDeleteAction} handleEditAction={handleEditAction}
        showApproveAction={false} showDeleteAction={'ADMIN' === role || 'LFHADMIN' === role} extBtnAction={exportData} 
        showExtBtn={false} extBtnText={'Export'} showDuplicateAction={true} handleDuplicateAction={createCopy}
        getRowId={(row: any) => row.rowId} addRow={addMasterData} showAddBtn={true} maxHeight={400}/>
    </>
  }

  return <div className="master-data">
    <TitlePanel title='Master Data' panelBody={getMainPanel()} />
    <IDialogComponent onClose={handleClose} open={open} title='Specifications' dialogClass={'spec-panel'} 
      dialogBody={<SpecsComponent data={rowData} />} />
  </div>
}

export default MasterDataList;